import React, { useState, useRef, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "sonner";
import {
  CalendarDays, Clock, Users, Minus, Plus, ShieldCheck, PenTool,
  CreditCard, Check, ArrowRight, ArrowLeft, Eraser, Cake, Star, Gift, Info, Ticket,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import GiftCardPurchase from "@/components/GiftCardPurchase";
import { useContent } from "@/context/ContentContext";
import { ESIGN_CONSENT_TEXT, DEFAULT_REFUND, fillEmail } from "@/lib/policies";

const WEEKDAY_SESSIONS = {
  0: ["1:00 PM", "3:30 PM", "6:00 PM"],                 // Sunday
  1: ["6:00 PM"], 2: ["6:00 PM"], 3: ["6:00 PM"], 4: ["6:00 PM"], 5: ["6:00 PM"], // Mon–Fri
  6: ["10:00 AM", "12:30 PM", "3:00 PM", "5:30 PM"],    // Saturday
};
const sessionsForDate = (dateStr) => {
  if (!dateStr) return [];
  const [y, m, d] = dateStr.split("-").map(Number);
  return WEEKDAY_SESSIONS[new Date(y, m - 1, d).getDay()] || [];
};
const EVENT_TYPES = ["Birthday Party", "Family Reunion", "Holiday Party", "School Trip", "Youth Group", "Corporate Event", "Church Group", "Team Celebration"];
const HERO_CATS = ["Healthcare", "Education", "First Responders", "Military"];
const STEPS = ["Eligibility", "Date & Time", "Details", "Agreement", "Payment"];

const field = "w-full bg-[#1a140e] border border-[rgba(211,218,221,0.16)] rounded-xl px-4 py-3 text-[#f6ecd4] placeholder:text-[#7f9a9a] outline-none focus:border-[#2fd0c6] focus:bg-[#241a11] focus:shadow-[0_0_0_3px_rgba(47,208,198,0.18)] transition";

function Stepper({ step }) {
  return (
    <ol className="flex items-center justify-center gap-2 sm:gap-3 mb-10 flex-wrap" aria-label={`Booking progress: step ${step + 1} of ${STEPS.length}, ${STEPS[step]}`} data-testid="booking-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-2 sm:gap-3" aria-current={i === step ? "step" : undefined}>
          <div className={`flex items-center gap-2 ${i <= step ? "text-[#ff6b6b]" : "text-[#9aa0c0]"}`}>
            <span className={`h-8 w-8 grid place-items-center rounded-full font-mono-arcade text-sm border ${i < step ? "bg-[#ff3b3b] text-white border-[#ff3b3b]" : i === step ? "border-[#ff3b3b]" : "border-[rgba(211,218,221,0.14)]"}`}>
              {i < step ? <Check size={15} /> : i + 1}
            </span>
            <span className="sr-only sm:not-sr-only text-xs font-semibold">{s}</span>
          </div>
          {i < STEPS.length - 1 && <span aria-hidden="true" className={`h-px w-4 sm:w-8 ${i < step ? "bg-[#ff3b3b]" : "bg-[rgba(211,218,221,0.14)]"}`} />}
        </li>
      ))}
    </ol>
  );
}

export default function Reservations() {
  const { content } = useContent();
  const pricing = content?.pricing || { base_hours: 2, base_price: 350, additional_hour_price: 125, hero_discount: 50 };
  const giftEnabled = content?.settings?.gift_cards_enabled;
  const discount = content?.discount;
  const agreement = content?.agreement || {};
  const cancellationClause = fillEmail(content?.refund_policy?.body || DEFAULT_REFUND.body, content?.contact?.email);

  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("book");
  const [loading, setLoading] = useState(false);
  const sigRef = useRef(null);

  const [form, setForm] = useState({
    birthdate: "", age_confirmed: false, reservation_date: "", start_time: "", hours: 2, event_type: "Birthday Party",
    name: "", email: "", phone: "", guest_count: "", hero_discount: false, hero_category: "",
    signer_name: "", agreement_accepted: false, signature_data: "",
    waiver_accepted: false, waiver_minor: false, waiver_minor_name: "", esign_consent: false,
    gift_code: "", gift_valid: false, gift_balance: 0,
    promo_code: "", promo_valid: false, promo_type: "", promo_value: 0,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [waiverContent, setWaiverContent] = useState(null);
  useEffect(() => { api.get("/waiver-content").then((r) => setWaiverContent(r.data)).catch(() => {}); }, []);

  const applyGift = async () => {
    const code = (form.gift_code || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post("/giftcards/redeem", { code });
      setForm((f) => ({ ...f, gift_valid: true, gift_balance: data.balance, gift_code: data.code }));
      toast.success(`Gift card applied! $${data.balance} available.`);
    } catch (err) {
      setForm((f) => ({ ...f, gift_valid: false, gift_balance: 0 }));
      toast.error(formatApiError(err?.response?.data?.detail) || "Invalid gift card code.");
    }
  };

  const applyPromo = async () => {
    const code = (form.promo_code || "").trim();
    if (!code) return;
    const addCost = Math.max(0, form.hours - pricing.base_hours) * pricing.additional_hour_price;
    const heroDisc = form.hero_discount ? pricing.hero_discount : 0;
    const subtotal = Math.max(0, pricing.base_price + addCost - heroDisc);
    try {
      const { data } = await api.post("/discounts/validate", { code, subtotal });
      setForm((f) => ({ ...f, promo_valid: true, promo_code: data.code, promo_type: data.type, promo_value: data.amount }));
      toast.success(`Promo applied! ${data.type === "percent" ? data.amount + "% off" : "$" + data.amount + " off"}.`);
    } catch (err) {
      setForm((f) => ({ ...f, promo_valid: false, promo_value: 0 }));
      toast.error(formatApiError(err?.response?.data?.detail) || "Invalid promo code.");
    }
  };

  const breakdown = useMemo(() => {
    const add = Math.max(0, form.hours - pricing.base_hours);
    const addCost = add * pricing.additional_hour_price;
    const disc = form.hero_discount ? pricing.hero_discount : 0;
    const afterHero = Math.max(0, pricing.base_price + addCost - disc);
    const promo = form.promo_valid
      ? Math.min(afterHero, form.promo_type === "percent" ? Math.round(afterHero * form.promo_value) / 100 : form.promo_value)
      : 0;
    const afterPromo = Math.max(0, afterHero - promo);
    const gift = form.gift_valid ? Math.min(form.gift_balance, afterPromo) : 0;
    const total = Math.max(0, afterPromo - gift);
    return { add, addCost, disc, promo, gift, total };
  }, [form.hours, form.hero_discount, form.gift_valid, form.gift_balance, form.promo_valid, form.promo_type, form.promo_value, pricing]);

  const age = useMemo(() => {
    if (!form.birthdate) return null;
    const bd = new Date(form.birthdate); const t = new Date();
    let a = t.getFullYear() - bd.getFullYear();
    if (t.getMonth() < bd.getMonth() || (t.getMonth() === bd.getMonth() && t.getDate() < bd.getDate())) a--;
    return a;
  }, [form.birthdate]);

  const today = new Date().toISOString().split("T")[0];
  const maxDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); d.setDate(d.getDate() - 1); return d.toISOString().split("T")[0]; })();

  const blackouts = content?.blackouts?.items || [];
  const dayBlackouts = blackouts.filter((b) => b.date === form.reservation_date);
  const fullDayBlocked = dayBlackouts.some((b) => !b.time);
  const blockedTimes = dayBlackouts.filter((b) => b.time).map((b) => b.time);
  const sessionTimes = sessionsForDate(form.reservation_date);

  const [takenTimes, setTakenTimes] = useState([]);
  useEffect(() => {
    if (!form.reservation_date) { setTakenTimes([]); return; }
    api.get(`/availability?date=${form.reservation_date}`).then((r) => setTakenTimes(r.data.taken || [])).catch(() => setTakenTimes([]));
  }, [form.reservation_date]);

  useEffect(() => {
    if (step !== 3) return;
    let cleanup = () => {};
    const t = setTimeout(() => {
      const canvas = sigRef.current?.getCanvas?.();
      if (!canvas) return;
      const resize = () => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const parent = canvas.parentElement;
        const w = parent ? parent.clientWidth : 600;
        canvas.width = w * ratio;
        canvas.height = 180 * ratio;
        const ctx = canvas.getContext("2d");
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(ratio, ratio);
        sigRef.current.clear();
      };
      resize();
      window.addEventListener("resize", resize);
      cleanup = () => window.removeEventListener("resize", resize);
    }, 80);
    return () => { clearTimeout(t); cleanup(); };
  }, [step]);

  const canNext = () => {
    if (step === 0) return form.birthdate && age !== null && age >= 18 && form.age_confirmed;
    if (step === 1) return form.reservation_date && form.start_time && sessionTimes.includes(form.start_time) && form.hours >= 2 && !fullDayBlocked && !blockedTimes.includes(form.start_time) && !takenTimes.includes(form.start_time);
    if (step === 2) return form.name && form.email && form.phone;
    if (step === 3) return form.signer_name.trim() && form.waiver_accepted && form.agreement_accepted && form.esign_consent && (!form.waiver_minor || form.waiver_minor_name.trim()) && sigRef.current && !sigRef.current.isEmpty();
    return true;
  };

  const handleNext = () => {
    if (!canNext()) {
      if (step === 3 && !form.esign_consent) { toast.error("Please check the box to consent to signing electronically."); return; }
      toast.error("Please complete this step."); return;
    }
    if (step === 3 && sigRef.current && !sigRef.current.isEmpty()) {
      set("signature_data", sigRef.current.getCanvas().toDataURL("image/png"));
    }
    setStep((s) => s + 1);
  };

  const goPay = async () => {
    if (!form.signer_name.trim() || !form.waiver_accepted || !form.agreement_accepted || !form.esign_consent || !form.signature_data) { toast.error("Please complete the waiver, agreement and signature."); return; }
    setLoading(true);
    try {
      const payload = { ...form, guest_count: form.guest_count ? Number(form.guest_count) : null, gift_card_code: form.gift_valid ? form.gift_code : null, discount_code: form.promo_valid ? form.promo_code : null };
      const { data } = await api.post("/reservations", payload);
      const { data: checkout } = await api.post("/payments/checkout", { reservation_id: data.reservation_id, origin_url: window.location.origin });
      window.location.href = checkout.checkout_url;
    } catch (err) {
      console.error("Checkout failed", err);
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen surface-alt">
      <Seo title="Reserve Your Party | Keys Event Center" description="Book the entire Keys Event Center facility. Choose your date, time and hours, sign the agreement, and pay securely online." />
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#f6ecd4]">Reserve Your Party</h1>
          <p className="text-[#a9bdbd] mt-3">The whole place, all yours. Let's get you booked.</p>
        </div>
        {giftEnabled && (
        <div className="flex justify-center gap-3 mb-8">
          <button onClick={() => setMode("book")} className={`px-6 py-2.5 ${mode === "book" ? "btn-primary" : "btn-outline"}`} data-testid="mode-book">Book a Party</button>
          <button onClick={() => setMode("gift")} className={`px-6 py-2.5 ${mode === "gift" ? "btn-primary" : "btn-outline"}`} data-testid="mode-gift">Buy a Gift Card</button>
        </div>
        )}
        {giftEnabled && mode === "gift" && <GiftCardPurchase />}
        {(!giftEnabled || mode === "book") && (<>
        <Stepper step={step} />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card p-6 sm:p-8">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>

                {step === 0 && (
                  <div data-testid="step-eligibility">
                    <h2 className="font-display text-2xl font-bold mb-2 flex items-center gap-2 text-[#f6ecd4]"><ShieldCheck className="text-[#ff3b3b]" /> Age Verification</h2>
                    <p className="text-[#a9bdbd] text-sm mb-6">You must be 18 or older to make a reservation.</p>
                    <label htmlFor="rs-birthdate" className="text-sm font-semibold text-[#f6ecd4]">Your Date of Birth</label>
                    <input id="rs-birthdate" type="date" aria-required="true" max={today} className={`${field} mt-2`} value={form.birthdate} onChange={(e) => set("birthdate", e.target.value)} data-testid="input-birthdate" />
                    {age !== null && age < 18 && <p role="alert" className="text-[#ff6b6b] text-sm mt-2">You must be at least 18 years old to reserve.</p>}
                    {age !== null && age >= 18 && <p className="text-[#2fd0c6] text-sm mt-2">You're eligible to book!</p>}
                    <label className="flex items-start gap-3 mt-6 cursor-pointer">
                      <input type="checkbox" checked={form.age_confirmed} onChange={(e) => set("age_confirmed", e.target.checked)} className="mt-1 accent-[#ff3b3b] h-4 w-4" data-testid="checkbox-age" />
                      <span className="text-sm text-[#a9bdbd]">I confirm that I am 18 years of age or older and authorized to make this reservation.</span>
                    </label>
                  </div>
                )}

                {step === 1 && (
                  <div data-testid="step-datetime">
                    <h2 className="font-display text-2xl font-bold mb-6 flex items-center gap-2 text-[#f6ecd4]"><CalendarDays className="text-[#ff3b3b]" /> Choose Date & Time</h2>
                    <label htmlFor="rs-date" className="text-sm font-semibold text-[#f6ecd4]">Reservation Date</label>
                    <input id="rs-date" type="date" aria-required="true" min={today} max={maxDate} className={`${field} mt-2 mb-5`} value={form.reservation_date} onChange={(e) => { set("reservation_date", e.target.value); set("start_time", ""); }} data-testid="input-date" />
                    {form.reservation_date && fullDayBlocked && (
                      <div className="rounded-xl p-3 mb-4 text-sm bg-[#2a1414] border border-[rgba(255,81,71,0.4)] text-[#ff5147]" data-testid="blackout-warning">
                        We're closed on this date. Please choose another day.
                      </div>
                    )}
                    <p id="rs-time-label" className="text-sm font-semibold text-[#f6ecd4] flex items-center gap-2"><Clock size={15} aria-hidden="true" /> Start Time</p>
                    {!form.reservation_date ? (
                      <p className="text-sm text-[#8fa5a5] mt-2 mb-6" data-testid="pick-date-hint">Pick a date above to see available session times.</p>
                    ) : (
                    <div role="group" aria-labelledby="rs-time-label" className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2 mb-6">
                      {sessionTimes.map((t) => {
                        const blocked = fullDayBlocked || blockedTimes.includes(t) || takenTimes.includes(t);
                        return (
                          <button key={t} disabled={blocked} aria-pressed={form.start_time === t} aria-label={blocked ? `${t}, unavailable` : t} onClick={() => set("start_time", t)} data-testid={`slot-${t.replace(/[^0-9]/g, "")}`}
                            className={`py-2.5 rounded-xl text-sm font-semibold border transition ${blocked ? "opacity-40 line-through cursor-not-allowed border-[rgba(211,218,221,0.14)] text-[#8fa5a5]" : form.start_time === t ? "bg-[#ff3b3b] text-white border-[#ff3b3b]" : "border-[rgba(211,218,221,0.14)] text-[#a9bdbd] hover:border-[#ff3b3b]"}`}>{t}</button>
                        );
                      })}
                    </div>
                    )}
                    <p className="text-sm font-semibold text-[#f6ecd4]">How Many Hours?</p>
                    <p className="text-xs text-[#8fa5a5] mb-3">Minimum 2 hours. Add as many as you like!</p>
                    <div className="flex items-center gap-4">
                      <button onClick={() => set("hours", Math.max(2, form.hours - 1))} aria-label="Remove one hour" className="h-11 w-11 grid place-items-center rounded-full border border-[rgba(211,218,221,0.14)] hover:border-[#ff3b3b] text-[#f6ecd4]" data-testid="hours-minus"><Minus size={18} /></button>
                      <div className="font-mono-arcade text-4xl font-extrabold text-[#f6ecd4] w-20 text-center" aria-live="polite" aria-label={`${form.hours} hours`} data-testid="hours-value">{form.hours}</div>
                      <button onClick={() => set("hours", form.hours + 1)} aria-label="Add one hour" className="h-11 w-11 grid place-items-center rounded-full border border-[rgba(211,218,221,0.14)] hover:border-[#ff3b3b] text-[#f6ecd4]" data-testid="hours-plus"><Plus size={18} /></button>
                      <span className="text-[#8fa5a5] text-sm">hours</span>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="step-details">
                    <h2 className="font-display text-2xl font-bold mb-6 flex items-center gap-2 text-[#f6ecd4]"><Users className="text-[#ff3b3b]" /> Your Details</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><label htmlFor="rs-name" className="text-sm font-semibold text-[#f6ecd4]">Full Name</label><input id="rs-name" autoComplete="name" aria-required="true" className={`${field} mt-2`} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="input-name" /></div>
                      <div><label htmlFor="rs-email" className="text-sm font-semibold text-[#f6ecd4]">Email</label><input id="rs-email" autoComplete="email" aria-required="true" type="email" className={`${field} mt-2`} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="input-email" /></div>
                      <div><label htmlFor="rs-phone" className="text-sm font-semibold text-[#f6ecd4]">Phone</label><input id="rs-phone" type="tel" autoComplete="tel" aria-required="true" className={`${field} mt-2`} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="input-phone" /></div>
                      <div><label htmlFor="rs-guests" className="text-sm font-semibold text-[#f6ecd4]">Guest Count <span className="text-[#8fa5a5] font-normal">(50 person max capacity)</span></label><input id="rs-guests" type="number" min="1" max="50" className={`${field} mt-2`} value={form.guest_count} onChange={(e) => { const v = e.target.value; set("guest_count", v === "" ? "" : String(Math.min(50, Math.max(1, Number(v))))); }} data-testid="input-guests" /></div>
                    </div>
                    <label htmlFor="rs-event-type" className="text-sm font-semibold text-[#f6ecd4] mt-4 block">Event Type</label>
                    <select id="rs-event-type" className={`${field} mt-2`} value={form.event_type} onChange={(e) => set("event_type", e.target.value)} data-testid="select-event-type">
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {discount && (
                      <div className="rounded-2xl p-5 mt-6 border-2 border-[rgba(246,182,74,0.4)] bg-[#241a11]" data-testid="hero-discount-block">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input type="checkbox" checked={form.hero_discount} onChange={(e) => set("hero_discount", e.target.checked)} className="mt-1 accent-[#f6b64a] h-4 w-4" data-testid="checkbox-hero-discount" />
                          <span>
                            <span className="font-semibold flex items-center gap-2 text-[#f6b64a]"><Star size={15} /> Apply ${discount.amount} HERO Discount</span>
                            <span className="text-xs text-[#a9bdbd] block mt-1">For Healthcare, Education, First Responders and Military. Verification may be required at check-in.</span>
                          </span>
                        </label>
                        {form.hero_discount && (
                          <select aria-label="HERO discount group" className={`${field} mt-3`} value={form.hero_category} onChange={(e) => set("hero_category", e.target.value)} data-testid="select-hero-category">
                            <option value="">Select your group…</option>
                            {HERO_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        )}
                      </div>
                    )}
                    <div className="rounded-2xl p-5 mt-4 border-2 border-[rgba(211,218,221,0.14)] bg-[#1a140e]" data-testid="giftcard-apply-block">
                      <label htmlFor="rs-gift" className="text-sm font-semibold text-[#f6ecd4] flex items-center gap-2"><Gift size={15} aria-hidden="true" className="text-[#2fd0c6]" /> Have a Gift Card?</label>
                      <div className="flex gap-2 mt-2">
                        <input id="rs-gift" className={field} placeholder="KEYS-XXXX-XXXX-XXXX" value={form.gift_code} onChange={(e) => set("gift_code", e.target.value)} data-testid="input-gift-code" />
                        <button type="button" onClick={applyGift} className="btn-outline px-5 shrink-0" data-testid="gift-apply-btn">Apply</button>
                      </div>
                      {form.gift_valid && <p className="text-[#2fd0c6] text-sm mt-2" data-testid="gift-applied-msg">Gift card applied - ${form.gift_balance} available.</p>}
                    </div>
                    <div className="rounded-2xl p-5 mt-4 border-2 border-[rgba(211,218,221,0.14)] bg-[#1a140e]" data-testid="promo-apply-block">
                      <label htmlFor="rs-promo" className="text-sm font-semibold text-[#f6ecd4] flex items-center gap-2"><Ticket size={15} aria-hidden="true" className="text-[#f6b64a]" /> Have a Promo Code?</label>
                      <div className="flex gap-2 mt-2">
                        <input id="rs-promo" className={field} placeholder="e.g. SUMMER20" value={form.promo_code} onChange={(e) => set("promo_code", e.target.value)} data-testid="input-promo-code" />
                        <button type="button" onClick={applyPromo} className="btn-outline px-5 shrink-0" data-testid="promo-apply-btn">Apply</button>
                      </div>
                      {form.promo_valid && <p className="text-[#f6b64a] text-sm mt-2" data-testid="promo-applied-msg">Promo applied - {form.promo_type === "percent" ? `${form.promo_value}% off` : `$${form.promo_value} off`}.</p>}
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div data-testid="step-agreement">
                    <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2 text-[#f6ecd4]"><PenTool className="text-[#ff3b3b]" /> Waiver &amp; Agreement</h2>
                    <p className="text-[#a9bdbd] text-sm mb-6">Please review both the liability waiver and the reservation agreement, then sign once below to accept both.</p>

                    <div className="mb-2" data-testid="waiver-section">
                      <h3 className="font-display text-lg font-bold text-[#f6ecd4] flex items-center gap-2 mb-2"><ShieldCheck size={18} className="text-[#f6b64a]" /> {waiverContent?.title || "Liability Waiver"}</h3>
                      <div tabIndex={0} role="region" aria-label="Liability waiver text" className="surface-alt rounded-2xl p-5 max-h-56 overflow-y-auto text-sm text-[#a9bdbd] whitespace-pre-line border border-[rgba(211,218,221,0.14)]" data-testid="waiver-text">{waiverContent?.body || "Loading…"}</div>
                      <div className="rounded-xl bg-[#1a140e] border border-[rgba(211,218,221,0.14)] p-4 mt-3 text-sm text-[#a9bdbd] leading-relaxed" data-testid="waiver-participant-summary">
                        Participant: <span className="text-[#f6ecd4] font-semibold">{form.name || "—"}</span> · DOB: <span className="text-[#f6ecd4] font-semibold">{form.birthdate || "—"}</span> · Date signed: <span className="text-[#f6ecd4] font-semibold">{today}</span>
                      </div>
                      <label className="flex items-center gap-3 mt-3 cursor-pointer select-none" data-testid="waiver-minor-toggle">
                        <input type="checkbox" checked={form.waiver_minor} onChange={(e) => set("waiver_minor", e.target.checked)} className="accent-[#ff3b3b] h-4 w-4" data-testid="checkbox-waiver-minor" />
                        <span className="text-sm text-[#a9bdbd]">This reservation includes a participant under 18</span>
                      </label>
                      {form.waiver_minor && (
                        <div className="mt-3" data-testid="waiver-minor-fields">
                          <label htmlFor="rs-minor-name" className="text-sm font-semibold text-[#f6ecd4]">Minor participant's name</label>
                          <input id="rs-minor-name" aria-required="true" className={`${field} mt-2`} value={form.waiver_minor_name} onChange={(e) => set("waiver_minor_name", e.target.value)} placeholder="Minor's full name" data-testid="input-waiver-minor-name" />
                          <p className="text-xs text-[#8fa5a5] mt-2">As the signing adult, you confirm you are the parent/legal guardian and consent to the minor's participation.</p>
                        </div>
                      )}
                      <label className="flex items-start gap-3 mt-4 cursor-pointer">
                        <input type="checkbox" checked={form.waiver_accepted} onChange={(e) => set("waiver_accepted", e.target.checked)} className="mt-1 accent-[#f6b64a] h-4 w-4" data-testid="checkbox-waiver" />
                        <span className="text-sm text-[#a9bdbd]">I have read and agree to the Liability Waiver above{form.waiver_minor ? ", and as parent/legal guardian I give permission for the named minor to participate" : ""}.</span>
                      </label>
                    </div>

                    <div className="h-px bg-[rgba(211,218,221,0.14)] my-6" />

                    <div className="mb-2" data-testid="agreement-section">
                      <h3 className="font-display text-lg font-bold text-[#f6ecd4] flex items-center gap-2 mb-2"><PenTool size={18} className="text-[#ff3b3b]" /> {agreement.title || "Reservation Agreement"}</h3>
                      <div tabIndex={0} role="region" aria-label="Reservation agreement text" className="surface-alt rounded-2xl p-5 max-h-56 overflow-y-auto text-sm text-[#a9bdbd] whitespace-pre-line border border-[rgba(211,218,221,0.14)]" data-testid="agreement-text">{agreement.body}</div>
                      <label className="flex items-start gap-3 mt-4 cursor-pointer">
                        <input type="checkbox" checked={form.agreement_accepted} onChange={(e) => set("agreement_accepted", e.target.checked)} className="mt-1 accent-[#ff3b3b] h-4 w-4" data-testid="checkbox-agreement" />
                        <span className="text-sm text-[#a9bdbd]">I have read and agree to the Keys Event Center Reservation Agreement.</span>
                      </label>
                    </div>

                    <div className="h-px bg-[rgba(211,218,221,0.14)] my-6" />

                    <label htmlFor="rs-signer" className="text-sm font-semibold text-[#f6ecd4]">Type Your Full Legal Name</label>
                    <input id="rs-signer" autoComplete="name" aria-required="true" className={`${field} mt-2 mb-4`} value={form.signer_name} onChange={(e) => set("signer_name", e.target.value)} placeholder="e.g. Jane A. Smith" data-testid="input-signer-name" />
                    <p id="rs-sig-label" className="text-sm font-semibold text-[#f6ecd4] mb-2 block">Draw Your Signature <span className="font-normal text-[#8fa5a5]">(finger, stylus or mouse)</span></p>
                    <div role="group" aria-labelledby="rs-sig-label" className="rounded-2xl border border-[rgba(211,218,221,0.14)] bg-white overflow-hidden relative" style={{ height: 180 }} data-testid="signature-pad">
                      <SignatureCanvas ref={sigRef} penColor="#0a0a0a" canvasProps={{ className: "sig-canvas", role: "img", "aria-label": "Signature drawing area", style: { width: "100%", height: "180px", display: "block", touchAction: "none" } }} />
                      <button type="button" onClick={() => sigRef.current?.clear()} className="absolute top-2 right-2 text-xs flex items-center gap-1 bg-[#ff5147] text-white px-2 py-1 rounded-lg" aria-label="Clear signature" data-testid="signature-clear"><Eraser size={12} aria-hidden="true" /> Clear</button>
                    </div>
                    <p className="text-xs text-[#8fa5a5] mt-3">Your electronic signature is legally binding and applies to both the Liability Waiver and the Reservation Agreement above.</p>
                    <label className="flex items-start gap-3 mt-4 cursor-pointer">
                      <input type="checkbox" checked={form.esign_consent} onChange={(e) => set("esign_consent", e.target.checked)} className="mt-1 accent-[#ff3b3b] h-4 w-4 shrink-0" data-testid="checkbox-esign-consent" />
                      <span className="text-sm text-[#a9bdbd]">{ESIGN_CONSENT_TEXT} <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-[#f6ecd4]">Privacy Policy</a></span>
                    </label>
                  </div>
                )}

                {step === 4 && (
                  <div data-testid="step-payment">
                    <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2 text-[#f6ecd4]"><CreditCard className="text-[#ff3b3b]" /> Secure Payment</h2>
                    <p className="text-[#a9bdbd] text-sm mb-6">Review your reservation, then continue to our secure Stripe checkout. Cards, Apple Pay and Google Pay accepted.</p>
                    <div className="space-y-3 text-sm">
                      <Row label="Event" value={form.event_type} />
                      <Row label="Date" value={form.reservation_date} />
                      <Row label="Start Time" value={form.start_time} />
                      <Row label="Duration" value={`${form.hours} hours`} />
                      <Row label="Name" value={form.name} />
                      {form.hero_discount && <Row label="HERO Discount" value={form.hero_category || "Applied"} />}
                      <Row label="Total due today" value={`$${breakdown.total}`} />
                    </div>
                    {cancellationClause && (
                      <div className="rounded-xl p-4 mt-5 text-sm text-[#a9bdbd] border border-[rgba(246,182,74,0.3)] bg-[#241a11]" data-testid="payment-cancellation-policy">
                        <p className="font-semibold text-[#f6b64a] mb-1">{content?.refund_policy?.title || DEFAULT_REFUND.title}</p>
                        <p>{cancellationClause} <a href="/privacy#refunds" target="_blank" rel="noreferrer" className="underline text-[#f6ecd4]">Full policies</a></p>
                      </div>
                    )}
                    <div className="surface-alt rounded-xl p-4 mt-5 flex items-center gap-3 text-sm text-[#a9bdbd] border border-[rgba(211,218,221,0.14)]">
                      <ShieldCheck className="text-[#2fd0c6]" size={20} /> Your payment is processed securely by Stripe. You'll receive a confirmation email instantly.
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-between mt-8">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="btn-outline px-6 py-3 flex items-center gap-2 disabled:opacity-40" data-testid="btn-back"><ArrowLeft size={16} /> Back</button>
              {step < STEPS.length - 1 ? (
                <button onClick={handleNext} className="btn-primary px-8 py-3 flex items-center gap-2" data-testid="btn-next">Continue <ArrowRight size={16} /></button>
              ) : (
                <button onClick={goPay} disabled={loading} className="btn-primary px-8 py-3 flex items-center gap-2 disabled:opacity-60" data-testid="btn-pay">{loading ? "Processing…" : <>Pay ${breakdown.total} <ArrowRight size={16} /></>}</button>
              )}
            </div>
          </div>

          <div className="lg:sticky lg:top-28 h-fit">
            <div className="card p-6" data-testid="price-summary">
              <div className="flex items-center gap-2 mb-4"><Cake className="text-[#ff3b3b]" /><h3 className="font-display font-bold text-lg text-[#f6ecd4]">Your Reservation</h3></div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-[#a9bdbd]"><span>Base (2 hours)</span><span className="font-mono-arcade">${pricing.base_price}</span></div>
                <div className="flex justify-between text-[#a9bdbd]"><span>Plus {breakdown.add} extra hour{breakdown.add !== 1 ? "s" : ""}</span><span className="font-mono-arcade">${breakdown.addCost}</span></div>
                {breakdown.disc > 0 && <div className="flex justify-between text-[#f6b64a]"><span>HERO Discount</span><span className="font-mono-arcade">-${breakdown.disc}</span></div>}
                {breakdown.promo > 0 && <div className="flex justify-between text-[#f6b64a]" data-testid="summary-promo"><span>Promo{form.promo_valid && form.promo_type === "percent" ? ` (${form.promo_value}%)` : ""}</span><span className="font-mono-arcade">-${breakdown.promo}</span></div>}
                {breakdown.gift > 0 && <div className="flex justify-between text-[#2fd0c6]"><span>Gift Card</span><span className="font-mono-arcade">-${breakdown.gift}</span></div>}
              </div>
              <div className="h-px bg-[rgba(211,218,221,0.14)] my-4" />
              <div className="flex justify-between items-center">
                <span className="font-semibold text-[#f6ecd4]">Total</span>
                <span className="font-mono-arcade text-3xl font-extrabold text-[#ff3b3b]" aria-live="polite" data-testid="summary-total">${breakdown.total}</span>
              </div>
              <p className="text-xs text-[#8fa5a5] mt-4">Two hour minimum. Plus ${pricing.additional_hour_price} per additional hour. No maximum.</p>
              <div className="mt-4 rounded-xl border border-[rgba(246,182,74,0.3)] bg-[#241a11] p-4" data-testid="additional-fees-note">
                <div className="flex items-center gap-2 text-[#f6b64a] font-semibold text-sm mb-2"><Info size={15} /> Possible Additional Fees</div>
                <ul className="text-xs text-[#a9bdbd] space-y-1">
                  <li className="flex justify-between"><span>Cleanup fee</span><span className="font-mono-arcade text-[#f6ecd4]">$25</span></li>
                  <li className="flex justify-between"><span>Late departure fee</span><span className="font-mono-arcade text-[#f6ecd4]">$35</span></li>
                </ul>
                <p className="text-xs text-[#8fa5a5] mt-2 leading-relaxed">Guests are responsible for any damage to the facility. These fees only apply if incurred and are not charged upfront.</p>
              </div>
            </div>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-[rgba(211,218,221,0.1)] pb-2">
      <span className="text-[#8fa5a5]">{label}</span>
      <span className="font-semibold text-[#f6ecd4]">{value || "-"}</span>
    </div>
  );
}
